import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentType, v4Service, ozowService, CreateSessionResponse } from "src/api";
import { onlyNumbers, formatNumberToExchange, generateDepositCallbackUrls, _t } from "src/utils";
import { ERoutes } from "src/types";

interface UseDepositActionsParams {
  amount: string;
  setAmount: (amount: string) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSessionData: (data: CreateSessionResponse) => void;
  currencyPrecision: number;
  paymentRouteId: string | null;
}

export const useDepositActions = (params: UseDepositActionsParams) => {
  const { amount, setAmount, setIsLoading, setError, setSessionData, currencyPrecision, paymentRouteId } = params;

  const navigate = useNavigate();

  /**
   * Handles amount input change with validation
   */
  const handleAmountChange = useCallback(
    (newAmount: string) => {
      if (onlyNumbers(newAmount)) {
        setAmount(formatNumberToExchange(newAmount, currencyPrecision));
      }
    },
    [currencyPrecision, setAmount]
  );

  /**
   * Creates a session for the deposit
   */
  const createSession = useCallback(
    async (amount: string) => {
      if (!paymentRouteId) {
        throw new Error(_t("ERRORS.PAYMENT_ROUTE_ID_REQUIRED"));
      }

      return await v4Service.createSession({
        data: {
          amount: Number(amount),
          payment_route_id: paymentRouteId,
          type: PaymentType.Deposit,
        },
      });
    },
    [paymentRouteId]
  );

  /**
   * Initiates the deposit with Ozow
   */
  const initiateDeposit = useCallback(async ({ exchange, sessionId }: { exchange: string; sessionId: string }) => {
    const redirectUrls = generateDepositCallbackUrls();

    return await ozowService.initiateDeposit({
      exchange,
      session_id: sessionId,
      ...redirectUrls,
    });
  }, []);

  /**
   * Main handler for initiating the deposit process
   * Creates session, initiates deposit with Ozow, and navigates to redirect page
   */
  const handleInitiateDeposit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      try {
        setIsLoading(true);
        setError(null);

        const sessionData = await createSession(amount);

        if (sessionData?.session_id && sessionData?.exchange) {
          setSessionData(sessionData);

          const ozowData = await initiateDeposit({
            exchange: sessionData.exchange,
            sessionId: sessionData.session_id,
          });

          if (ozowData?.redirectUrl) {
            // Navigate to payment redirect page with the Ozow URL
            navigate(`${ERoutes.PaymentRedirect}?redirectUrl=${encodeURIComponent(ozowData.redirectUrl)}`);
          }
        }
      } catch (error) {
        console.error("Error initiating deposit:", error);
        setError(_t("ERRORS.FAILED_TO_INITIATE_DEPOSIT"));
      } finally {
        setIsLoading(false);
      }
    },
    [amount, createSession, initiateDeposit, navigate, setError, setIsLoading, setSessionData]
  );

  return {
    handleAmountChange,
    handleInitiateDeposit,
  };
};
